import React from "react";
import { Link } from "react-router-dom";
import { Title } from "@ui5/webcomponents-react";

const LAST_UPDATED = "March 31, 2026";

const Section = ({ number, title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-[#0854a0] mb-3">
      {number}. {title}
    </h2>
    <div className="text-gray-700 space-y-2 leading-relaxed">{children}</div>
  </div>
);

const RightsList = ({ items }) => (
  <ul className="list-disc list-inside space-y-1 mt-2">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      {/* Top bar */}
      <header
        style={{
          backgroundColor: "#0854a0",
          padding: "1rem 2rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <Title level="H4" style={{ color: "#ffffff", margin: 0 }}>
          conRIO
        </Title>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.875rem" }}>
          Privacy Policy
        </span>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-10">
        {/* Page title */}
        <div className="mb-8 pb-6 border-b border-gray-200">
          <Title level="H2" className="text-gray-900 mb-2">
            Privacy Policy
          </Title>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <p className="text-gray-700 mb-8 leading-relaxed">
          This Privacy Policy describes how Concircle GmbH ("Concircle", "we",
          "our", or "us") collects, uses, and protects personal data when you
          use the conRIO (concircle Release Impact Overview) application, a SaaS
          solution for SAP Digital Manufacturing change impact analysis.
        </p>

        <Section number="1" title="Data Controller">
          <p>The data controller responsible for your personal data is:</p>
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            <strong>Concircle GmbH</strong>
            <br />
            Austria
            <br />
            Email:{" "}
            <a
              href="mailto:support@concircle.com"
              className="text-[#0854a0] hover:underline"
            >
              support@concircle.com
            </a>
          </div>
        </Section>

        <Section number="2" title="What Data We Collect">
          <p>We collect the following categories of personal data:</p>
          <RightsList
            items={[
              "User account data: name, email address, and assigned role (admin / consultant / user)",
              "Customer organisation data: company name, assigned services, and company logos",
              "Usage data: login timestamps and actions performed within the application",
              "Uploaded files: customer logos stored in Azure Blob Storage",
            ]}
          />
        </Section>

        <Section number="3" title="Purpose of Processing">
          <p>We process your personal data for the following purposes:</p>
          <RightsList
            items={[
              "Providing the conRIO SaaS service (change impact analysis for SAP DM releases)",
              "User authentication and access control",
              "Generation of change impact reports",
              "Automated synchronisation with SAP Help Portal documentation",
            ]}
          />
        </Section>

        <Section number="4" title="Legal Basis (GDPR Article 6)">
          <p>Processing of your personal data is based on:</p>
          <RightsList
            items={[
              "Performance of a contract — processing is necessary to provide the service you have contracted (Art. 6(1)(b))",
              "Legal obligation — processing necessary for compliance with applicable legal requirements (Art. 6(1)(c))",
              "Legitimate interests — processing based on our legitimate interests in operating and improving the service (Art. 6(1)(f))",
            ]}
          />
        </Section>

        <Section number="5" title="Data Storage and Retention">
          <RightsList
            items={[
              "All data is stored on Microsoft Azure infrastructure in EU regions",
              "User and customer data is retained for the duration of the contractual relationship",
              "Data is deleted within 30 days of account or contract termination",
              "Uploaded files (logos, documents) are stored in Azure Blob Storage and deleted upon account termination",
            ]}
          />
        </Section>

        <Section number="6" title="Third Parties">
          <RightsList
            items={[
              "Microsoft Azure: our infrastructure and hosting provider (EU data centres)",
              "No personal data is sold, rented, or shared with third parties for marketing purposes",
              "SAP Help Portal is accessed for read-only documentation synchronisation — no personal data is transmitted",
            ]}
          />
        </Section>

        <Section number="7" title="Your Rights Under GDPR">
          <p>
            As a data subject under the General Data Protection Regulation
            (GDPR), you have the following rights regarding your personal data:
          </p>
          <RightsList
            items={[
              "Right of access — obtain a copy of your personal data (Art. 15)",
              "Right to rectification — correct inaccurate personal data (Art. 16)",
              'Right to erasure / "right to be forgotten" (Art. 17)',
              "Right to restriction of processing (Art. 18)",
              "Right to data portability (Art. 20)",
              "Right to object to processing (Art. 21)",
              "Right to lodge a complaint with the Austrian data protection authority (Datenschutzbehörde — dsb.gv.at)",
            ]}
          />
          <p className="mt-3">
            To exercise any of these rights, please contact us at{" "}
            <a
              href="mailto:support@concircle.com"
              className="text-[#0854a0] hover:underline"
            >
              support@concircle.com
            </a>
            .
          </p>
        </Section>

        <Section number="8" title="Cookies and Tracking">
          <RightsList
            items={[
              "The application uses only technically necessary session mechanisms (JWT tokens stored in memory or localStorage)",
              "No advertising cookies are used",
              "No third-party tracking or analytics services are integrated",
            ]}
          />
        </Section>

        <Section number="9" title="Changes to This Policy">
          <p>
            This privacy policy may be updated periodically to reflect changes
            in our practices or applicable law. The date of the last update is
            shown at the top of this page. Continued use of the service after an
            update constitutes acceptance of the revised policy.
          </p>
        </Section>

        <Section number="10" title="Contact">
          <p>
            For any privacy-related questions, requests, or concerns, please
            contact us at:
          </p>
          <div className="mt-3">
            <a
              href="mailto:support@concircle.com"
              className="text-[#0854a0] hover:underline font-medium"
            >
              support@concircle.com
            </a>
          </div>
        </Section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-6 px-6">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            &copy; {new Date().getFullYear()} Concircle GmbH. All rights
            reserved.
          </span>
          <Link to="/" className="text-[#0854a0] hover:underline">
            Back to conRIO
          </Link>
        </div>
      </footer>
    </div>
  );
}
